/**
 * Claude Code 実行イベント dashboard panel.
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Claude Code 実行イベント (tool/skill/command/action) のサマリ・event_name ランキング・メンバー別・チケット別・時系列を 1 画面に描画する presentation component。fetch は親が行い data を渡す
 * @graph-connects none
 */

import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import { TabButton, TabGroup } from '../../ui/Tabs/index.js';
import { formatRepositoryName } from '../cc-usage/formatters.js';
import type {
  CcEventsEventType,
  CcEventsNameBreakdownResponse,
  CcEventsProjectBreakdownResponse,
  CcEventsSummaryResponse,
  CcEventsTimeseriesResponse,
  CcEventsUserBreakdownResponse,
} from '../../../types/api.js';

export interface CcEventsPanelProps {
  summary: CcEventsSummaryResponse | undefined;
  /** summary fetch 中フラグ */
  isSummaryLoading: boolean;
  timeseries: CcEventsTimeseriesResponse | undefined;
  byName: CcEventsNameBreakdownResponse | undefined;
  byUser: CcEventsUserBreakdownResponse | undefined;
  byProject: CcEventsProjectBreakdownResponse | undefined;
  /** by-name ランキングの対象 event_type (UI タブの現在値) */
  eventType: CcEventsEventType;
  /** event_type タブ変更時のコールバック */
  onEventTypeChange: (value: CcEventsEventType) => void;
  /** cortex リポジトリを含めるか (チェック時 true = 含める。既定 false = 除外) */
  includeCortex: boolean;
  /** cortex トグル変更時のコールバック */
  onIncludeCortexChange: (value: boolean) => void;
  /** skill=slack-triage を含めるか (チェック時 true = 含める。既定 false = 除外) */
  includeSlackTriage: boolean;
  /** slack-triage トグル変更時のコールバック */
  onIncludeSlackTriageChange: (value: boolean) => void;
}

/** @graph-connects none */
const EVENT_TYPES: readonly CcEventsEventType[] = ['tool', 'skill', 'command', 'action'];

/** @graph-connects none */
function formatNumber(value: number): string {
  return value.toLocaleString();
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-events dashboard panel (presentation のみ)
 * @graph-connects TabGroup [calls] event_type 切替タブ描画
 */
export function CcEventsPanel({
  summary,
  isSummaryLoading,
  timeseries,
  byName,
  byUser,
  byProject,
  eventType,
  onEventTypeChange,
  includeCortex,
  onIncludeCortexChange,
  includeSlackTriage,
  onIncludeSlackTriageChange,
}: CcEventsPanelProps) {
  const { t } = useTranslation();

  if (isSummaryLoading) {
    return (
      <div className="h-48 flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  const metrics = [
    { label: t('ccEvents.metrics.totalEvents'), value: formatNumber(summary?.totalEvents ?? 0) },
    {
      label: t('ccEvents.metrics.aiCommitCount'),
      value: formatNumber(summary?.aiCommitCount ?? 0),
    },
    { label: t('ccEvents.metrics.aiPrCount'), value: formatNumber(summary?.aiPrCount ?? 0) },
    { label: t('ccEvents.metrics.uniqueUsers'), value: formatNumber(summary?.uniqueUsers ?? 0) },
  ];

  const timeseriesData = (timeseries?.data ?? []).map((point) => ({
    bucket: point.bucket,
    toolCount: point.toolCount,
    skillCount: point.skillCount,
    commandCount: point.commandCount,
    actionCount: point.actionCount,
  }));

  const nameRows = byName?.data ?? [];
  const userRows = byUser?.data ?? [];
  const projectRows = byProject?.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 cursor-pointer text-sm text-[var(--color-text-secondary)]">
          <Checkbox
            checked={includeCortex}
            onChange={(event) => {
              onIncludeCortexChange(event.target.checked);
            }}
          />
          <span>{t('ccEvents.filters.includeCortex')}</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-[var(--color-text-secondary)]">
          <Checkbox
            checked={includeSlackTriage}
            onChange={(event) => {
              onIncludeSlackTriageChange(event.target.checked);
            }}
          />
          <span>{t('ccEvents.filters.includeSlackTriage')}</span>
        </label>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {metrics.map((metric) => (
          <Card key={metric.label}>
            <CardContent className="flex flex-col gap-1 px-4 py-3">
              <span className="text-xs text-[var(--color-text-muted)]">{metric.label}</span>
              <span className="text-lg font-semibold text-[var(--color-text-primary)]">
                {metric.value}
              </span>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
            {t('ccEvents.timeseries.title')}
          </h2>
        </CardHeader>
        <CardContent className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={timeseriesData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="bucket" stroke="var(--color-text-muted)" />
              <YAxis stroke="var(--color-text-muted)" />
              <Tooltip
                wrapperStyle={{ zIndex: 50, outline: 'none' }}
                formatter={(value) =>
                  typeof value === 'number' ? formatNumber(value) : String(value ?? '')
                }
                contentStyle={{
                  backgroundColor: 'var(--color-bg-elevated)',
                  borderColor: 'var(--color-border)',
                  borderRadius: '8px',
                  boxShadow: 'var(--shadow-lg)',
                }}
              />
              <Legend />
              <Bar
                dataKey="toolCount"
                name={t('ccEvents.eventType.tool')}
                stackId="events"
                fill="#6366f1"
              />
              <Bar
                dataKey="skillCount"
                name={t('ccEvents.eventType.skill')}
                stackId="events"
                fill="#22c55e"
              />
              <Bar
                dataKey="commandCount"
                name={t('ccEvents.eventType.command')}
                stackId="events"
                fill="#f59e0b"
              />
              <Bar
                dataKey="actionCount"
                name={t('ccEvents.eventType.action')}
                stackId="events"
                fill="#ec4899"
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
              {t('ccEvents.breakdown.byName')}
            </h2>
            <TabGroup ariaLabel={t('ccEvents.eventTypeSwitchLabel')}>
              {EVENT_TYPES.map((value) => (
                <TabButton
                  key={value}
                  active={eventType === value}
                  label={t(`ccEvents.eventType.${value}`)}
                  onClick={() => {
                    onEventTypeChange(value);
                  }}
                />
              ))}
            </TabGroup>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                  <th className="py-2 pr-4">{t('ccEvents.breakdown.eventName')}</th>
                  <th className="py-2 pr-4 text-right">{t('ccEvents.metrics.eventCount')}</th>
                  <th className="py-2 text-right">{t('ccEvents.metrics.uniqueUsers')}</th>
                </tr>
              </thead>
              <tbody>
                {nameRows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-4 text-center text-[var(--color-text-muted)]">
                      {t('ccEvents.empty')}
                    </td>
                  </tr>
                ) : (
                  nameRows.map((row) => (
                    <tr key={row.eventName} className="border-b border-[var(--color-border)]">
                      <td className="py-2 pr-4 text-[var(--color-text-primary)]">
                        {row.eventName}
                      </td>
                      <td className="py-2 pr-4 text-right text-[var(--color-text-primary)]">
                        {formatNumber(row.eventCount)}
                      </td>
                      <td className="py-2 text-right text-[var(--color-text-secondary)]">
                        {formatNumber(row.uniqueUsers)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
            {t('ccEvents.breakdown.byUser')}
          </h2>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                  <th className="py-2 pr-4">{t('ccEvents.breakdown.user')}</th>
                  <th className="py-2 pr-4">{t('ccEvents.breakdown.team')}</th>
                  <th className="py-2 pr-4 text-right">{t('ccEvents.metrics.totalEvents')}</th>
                  <th className="py-2 pr-4 text-right">{t('ccEvents.eventType.tool')}</th>
                  <th className="py-2 pr-4 text-right">{t('ccEvents.eventType.skill')}</th>
                  <th className="py-2 pr-4 text-right">{t('ccEvents.eventType.command')}</th>
                  <th className="py-2 pr-4 text-right">{t('ccEvents.metrics.aiCommitCount')}</th>
                  <th className="py-2 text-right">{t('ccEvents.metrics.aiPrCount')}</th>
                </tr>
              </thead>
              <tbody>
                {userRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-4 text-center text-[var(--color-text-muted)]">
                      {t('ccEvents.empty')}
                    </td>
                  </tr>
                ) : (
                  userRows.map((row, index) => (
                    <tr
                      key={`${row.email ?? '__unknown__'}-${String(index)}`}
                      className="border-b border-[var(--color-border)]"
                    >
                      <td className="py-2 pr-4 text-[var(--color-text-primary)]">
                        {row.nickname ?? t('ccEvents.breakdown.unassigned')}
                        {row.email ? (
                          <span className="text-xs text-[var(--color-text-muted)] ml-2">
                            ({row.email})
                          </span>
                        ) : null}
                      </td>
                      <td className="py-2 pr-4 text-[var(--color-text-secondary)]">
                        {row.teamName ?? t('ccEvents.breakdown.unassigned')}
                      </td>
                      <td className="py-2 pr-4 text-right text-[var(--color-text-primary)]">
                        {formatNumber(row.totalEvents)}
                      </td>
                      <td className="py-2 pr-4 text-right text-[var(--color-text-secondary)]">
                        {formatNumber(row.toolCount)}
                      </td>
                      <td className="py-2 pr-4 text-right text-[var(--color-text-secondary)]">
                        {formatNumber(row.skillCount)}
                      </td>
                      <td className="py-2 pr-4 text-right text-[var(--color-text-secondary)]">
                        {formatNumber(row.commandCount)}
                      </td>
                      <td className="py-2 pr-4 text-right text-[var(--color-text-secondary)]">
                        {formatNumber(row.aiCommitCount)}
                      </td>
                      <td className="py-2 text-right text-[var(--color-text-secondary)]">
                        {formatNumber(row.aiPrCount)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
            {t('ccEvents.breakdown.byProject')}
          </h2>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                  <th className="py-2 pr-4">{t('ccEvents.breakdown.ticket')}</th>
                  <th className="py-2 pr-4 text-right">{t('ccEvents.metrics.eventCount')}</th>
                  <th className="py-2 pr-4 text-right">{t('ccEvents.metrics.aiCommitCount')}</th>
                  <th className="py-2 pr-4 text-right">{t('ccEvents.metrics.aiPrCount')}</th>
                  <th className="py-2 text-right">{t('ccEvents.metrics.uniqueUsers')}</th>
                </tr>
              </thead>
              <tbody>
                {projectRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-4 text-center text-[var(--color-text-muted)]">
                      {t('ccEvents.empty')}
                    </td>
                  </tr>
                ) : (
                  projectRows.map((row) => (
                    <tr
                      key={row.ticketKey ?? row.repository ?? '__no_ticket__'}
                      className="border-b border-[var(--color-border)]"
                    >
                      <td className="py-2 pr-4 text-[var(--color-text-primary)]">
                        {row.ticketKey ??
                          (row.repository
                            ? t('ccEvents.breakdown.noTicketRepository', {
                                repository: formatRepositoryName(row.repository),
                              })
                            : t('ccEvents.breakdown.noTicketUnknown'))}
                        {row.ticketSummary ? (
                          <span className="text-xs text-[var(--color-text-muted)] ml-2">
                            {row.ticketSummary}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-2 pr-4 text-right text-[var(--color-text-primary)]">
                        {formatNumber(row.eventCount)}
                      </td>
                      <td className="py-2 pr-4 text-right text-[var(--color-text-secondary)]">
                        {formatNumber(row.aiCommitCount)}
                      </td>
                      <td className="py-2 pr-4 text-right text-[var(--color-text-secondary)]">
                        {formatNumber(row.aiPrCount)}
                      </td>
                      <td className="py-2 text-right text-[var(--color-text-secondary)]">
                        {formatNumber(row.uniqueUsers)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
